// Plain-English explanations for non-technical users.
// Turns the detailed verification result into a few simple sentences: is it safe,
// and why / why not. The ML breakdown and risk factors are still shown below.

// Map internal signals/flag titles to everyday phrases.
const REASON_PHRASES = {
  TYPOSQUATTING: 'the web address is spelled to look like a well-known brand but isn\u2019t the real one',
  DOMAIN_MISMATCH: 'it mentions a trusted brand, but the address doesn\u2019t actually belong to that company',
  SUSPICIOUS_CHARACTERS: 'the address uses look-alike characters to trick your eye',
  LOOKALIKE_DOMAIN: 'the address closely imitates a real brand\u2019s name',
  KNOWN_SUSPICIOUS: 'this address is on our list of known malicious sites',
  CREDENTIAL_REQUEST: 'it asks for your password \u2014 real companies never do this by email',
  OTP_REQUEST: 'it asks for a verification/OTP code \u2014 a classic account-takeover trick',
  DANGEROUS_LINK: 'it contains a link that looks dangerous',
  KNOWN_PHISHING_PATTERN: 'the sender address follows a common phishing pattern',
};

const FLAG_PHRASES = {
  'Suspicious TLD': 'it uses an unusual domain ending often linked to scams',
  'No HTTPS': 'the connection isn\u2019t encrypted',
  'URL Shortener': 'it hides the real destination behind a shortened link',
  'IP Address URL': 'it uses a raw IP address instead of a normal domain name',
  'Suspicious Subdomains': 'the real domain is buried behind extra sub-domains',
  'Redirect Chain': 'it looks like it bounces through redirects to hide where it goes',
  'Urgency/Threats': 'it pressures you to act immediately \u2014 a common scare tactic',
  'Requests Payment Info': 'it asks for card or bank details',
  'Emotional Manipulation': 'it uses fear or urgency to push you into acting',
  'Conspiracy/Suppression Framing': 'it claims powerful groups are hiding the truth',
  'Anonymous or Vague Sources': 'it doesn\u2019t cite named, verifiable sources',
  'Unsupported Statistics': 'it quotes statistics without saying where they come from',
  'Sensationalist Language': 'it leans on shocking, exaggerated wording',
  'Absolute Claims': 'it makes sweeping, absolute claims',
};

const topReasonPhrases = (result, max = 2) => {
  const out = [];
  (result.hardRedFlags || []).forEach((s) => { if (REASON_PHRASES[s]) out.push(REASON_PHRASES[s]); });
  (result.criticalSignals || []).forEach((s) => { if (REASON_PHRASES[s] && !out.includes(REASON_PHRASES[s])) out.push(REASON_PHRASES[s]); });
  (result.topReasons || []).forEach((f) => {
    const p = FLAG_PHRASES[f.title];
    if (p && !out.includes(p)) out.push(p);
  });
  return out.slice(0, max);
};

const joinReasons = (arr) => {
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0] + '.';
  return arr.slice(0, -1).join('; ') + '; and ' + arr[arr.length - 1] + '.';
};

export const explainLink = (result) => {
  const reasons = topReasonPhrases(result);
  if (result.verdict === 'Safe') {
    return {
      level: 'safe',
      headline: 'This link looks safe to open.',
      lines: ['Our checker didn\u2019t find impersonation tricks or dangerous patterns in the web address, and the model rates it low-risk.',
        'Still, only enter passwords or payment details on sites you know and trust.'],
    };
  }
  if (result.verdict === 'Caution') {
    return {
      level: 'caution',
      headline: 'Be careful with this link.',
      lines: [reasons.length ? `It isn\u2019t clearly dangerous, but a few things stand out: ${joinReasons(reasons)}`
        : 'It isn\u2019t clearly dangerous, but a few small things stand out.',
        'If you\u2019re unsure, type the official website address yourself instead of clicking.'],
    };
  }
  return {
    level: 'danger',
    headline: 'Don\u2019t click this link.',
    lines: [reasons.length ? `It shows signs of a phishing attempt: ${joinReasons(reasons)}`
      : 'It shows strong signs of being a malicious or phishing link.',
      'Do not enter any information, and report it if it was sent to you.'],
  };
};

export const explainEmail = (result) => {
  const reasons = topReasonPhrases(result);
  if (result.verdict === 'Likely Legitimate') {
    return {
      level: 'safe',
      headline: 'This email looks legitimate.',
      lines: ['We didn\u2019t spot the usual phishing tricks in the sender or the message.',
        'Even so, never share passwords or codes by email, and double-check unexpected requests.'],
    };
  }
  if (result.verdict === 'Suspicious') {
    return {
      level: 'caution',
      headline: 'Treat this email with caution.',
      lines: [reasons.length ? `A few things look off: ${joinReasons(reasons)}`
        : 'A few things about this email look off.',
        'Contact the company through their official website or app rather than replying.'],
    };
  }
  return {
    level: 'danger',
    headline: 'This looks like a phishing email.',
    lines: [reasons.length ? `Warning signs include: ${joinReasons(reasons)}`
      : 'It shows strong signs of a phishing attempt.',
      'Don\u2019t click links, open attachments, or share any details. Report it as phishing.'],
  };
};

export const explainArticle = (result) => {
  const reasons = topReasonPhrases(result);
  const trusted = result.trustedSource || (result.ml && result.ml.trustedSource);
  if (result.category === 'High Credibility') {
    return {
      level: 'safe',
      headline: 'This article looks credible.',
      lines: [trusted ? 'It comes from an established, reputable publisher and reads like balanced, well-sourced reporting.'
        : 'It reads like balanced, well-sourced reporting without the usual misinformation red flags.',
        'This is a model estimate, not a fact-check \u2014 verify key claims for anything important.'],
    };
  }
  if (result.category === 'Mixed / Uncertain') {
    return {
      level: 'caution',
      headline: 'This article has mixed signals.',
      lines: [reasons.length ? `Some parts raise questions: ${joinReasons(reasons)}`
        : 'Some parts read reasonably while others raise questions.',
        'Cross-check the main claims with other trusted sources before sharing. This is a model estimate, not a fact-check.'],
    };
  }
  return {
    level: 'danger',
    headline: 'This article looks low-credibility.',
    lines: [reasons.length ? `It shows classic misinformation traits: ${joinReasons(reasons)}`
      : 'It shows several classic misinformation traits.',
      'Be very skeptical and don\u2019t share it before verifying. This is a model estimate, not a fact-check.'],
  };
};

export const explainComparison = (resultA, resultB) => {
  const a = resultA.credibilityScore, b = resultB.credibilityScore;
  const level = Math.max(a, b) >= 80 ? 'safe' : Math.max(a, b) >= 50 ? 'caution' : 'danger';
  if (a === b) {
    return { level, headline: 'Both articles are about equally credible.',
      lines: [`They scored the same (${a}/100). Neither stands out as clearly more trustworthy \u2014 verify key claims in both.`] };
  }
  const winner = a > b ? 'A' : 'B';
  const wScore = Math.max(a, b), lScore = Math.min(a, b);
  return {
    level,
    headline: `Article ${winner} is the safer, more credible read.`,
    lines: [`Article ${winner} scored ${wScore}/100 versus ${lScore}/100 for the other \u2014 it shows fewer misinformation signals and/or better sourcing.`,
      'These are model estimates, not fact-checks \u2014 still verify important claims.'],
  };
};
